// --- Dependencies -------------------------------------------------------------------------------
import { NavLink } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyOverviewResponse } from "@/types";
import { formatNumber } from "./helpers";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    data: TyOverviewResponse;
};

type TyItem = { key: string; label: string; count: number; to: string; tone: "warn" | "bad" };

// --- Logic --------------------------------------------------------------------------------------

/**
 * The four things that mean somebody is waiting on Menuket: an unanswered ticket, a transfer nobody has
 * confirmed, a store whose menu has gone dark, and a 5xx a real café hit. They sit above every chart
 * because a dashboard that reports health without surfacing the work is a report, not a tool.
 *
 * Each entry is a link and each one disappears at zero — a row of reassuring zeroes trains an admin to
 * stop reading the row at all, which is exactly when the one non-zero day arrives.
 */
export default function AttentionRow({ data }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);

    const items: TyItem[] = [
        { key: "tickets", label: t.home.attentionTickets, count: data.support?.open ?? 0, to: "/support", tone: "bad" },
        { key: "payments", label: t.home.attentionPayments, count: data.subscriptions?.pendingCount ?? 0, to: "/subscriptions", tone: "warn" },
        { key: "paused", label: t.home.attentionPaused, count: data.stores?.paused ?? 0, to: "/stores", tone: "warn" },
        { key: "errors", label: t.home.attentionErrors, count: data.logs?.errors ?? 0, to: "/logs", tone: "bad" },
    ].filter((item) => item.count > 0) as TyItem[];

    if (!items.length)
        return (
            <div className="home-attention home-attention--clear">
                <span className="home-attention-dot" data-tone="good" />
                <p className="home-attention-clear-text">{t.home.attentionClear}</p>
            </div>
        );

    return (
        <div className="home-attention">
            <p className="home-attention-title">{t.home.attentionTitle}</p>
            <div className="home-attention-items">
                {items.map((item) => (
                    <NavLink key={item.key} to={item.to} className="home-attention-item" data-tone={item.tone}>
                        <span className="home-attention-count">{formatNumber(item.count, lang)}</span>
                        <span className="home-attention-label">{item.label}</span>
                    </NavLink>
                ))}
            </div>
        </div>
    );
}
